import { SectionPlacement } from '@/shared/sectionFrames'

interface SectionItemInput {
	name: string;
	center_section_to_field?: boolean;
	offsetX?: number | string;
	offsetY?: number | string;
	width?: number | string;
	height?: number | string;
	//priceModel?: any;
	//price?: number | string;
	sectionAngle?: number | string;
	radialAngle?: number | string;
	maintain_aspect_ratio?: boolean;
	preview_mark_speed?: number | string;
	marking_order?: number | string;
	horizontal_ratio_percentage?: number | string;
	vertical_ratio_percentage?: number | string;
	preview_mark_type?: string;
	outline_laser_profile_id?: string | number;
	hatch_profile_ids?: string[] | number[];
	thumbnail_image_url?: string;
	image?: string;
	targetObject: any;
	/** Frame geometry for section_model 'frame'; absent on cylindrical sections. */
	placement?: SectionPlacement | null
	groupName?: any
	centerRadialAngle: number
	engravingSetting?: any
	shapeType?: string | { value: string }
	rotation?: number | string
	radius?: number | string
	vertices?: { x: number; y: number }[]
	materialType?: string | { value: string }
	materialSource?: { mode?: 'color' | 'pattern'; color?: string; file?: { url?: string; data?: any } | null }
	engravingTint?: number | string
	overlayOpacity?: number | string
	graphicCategoryIds?: Array<{ value: string } | string>
	designTemplateIds?: Array<{ value: string } | string>
}


interface SectionItemOutput {
	name: string;
	center_section_to_field: boolean | null;
	default_location: { x: number; y: number; z: number };
	default_size: { width: number; height: number };
	//pricing_id: string;
	artwork_angle: number;
	radial_angle: number;
	maintain_aspect_ratio: boolean | null;
	preview_mark_speed: number | null;
	marking_order?: number | string | null;
	keystone_correction: { horizontal_ratio_percentage: number; vertical_ratio_percentage: number } | null;
	preview_mark_type?: string | null;
	outline_laser_profile_id?: string | number | null;
	hatch_profile_ids?: string[] | number[] | null;
	thumbnail_image_url?: string;
	getImg?: string;
	target_object: any;
	graphic_category_ids: string[];
	design_template_ids: string[];
}


const mapSectionToSave = (
	sections: SectionItemInput[],
	state: any,
	machine?: string | null,
	validate2DGeometry = false
): SectionItemOutput[] => {
	return sections.map((item, index) => {
		const isNeatolabs = machine === 'neatolabs'
		const shapeType = typeof item.shapeType === 'object' ? item.shapeType?.value : (item.shapeType || 'rectangle')
		const offsetX = Number(item.offsetX)
		const offsetY = Number(item.offsetY)
		const width = Number(item.width)
		const height = Number(item.height)

		const materialType = typeof item.materialType === 'object'
			? (item.materialType as { value: string })?.value
			: item.materialType
		// A custom material is a colour or a pattern, decided by `mode`. Only the
		// active one is written below, so an abandoned value never reaches the JSON.
		const materialSource = item.materialSource
		const usesPattern = materialSource?.mode === 'pattern'
		if (materialType === 'custom') {
			if (usesPattern && !materialSource?.file) {
				throw new Error(`Section "${item.name || index + 1}" uses a custom pattern but no image was chosen.`)
			}
			if (!usesPattern && !materialSource?.color) {
				throw new Error(`Section "${item.name || index + 1}" uses a custom colour but none was chosen.`)
			}
		}

		if (validate2DGeometry && (
			!Number.isFinite(offsetX) ||
			!Number.isFinite(offsetY) ||
			!Number.isFinite(width) ||
			!Number.isFinite(height) ||
			width <= 0 ||
			height <= 0
		)) {
			throw new Error(`Section "${item.name || index + 1}" has invalid or missing geometry.`)
		}

		return {
			name: item.name,
			graphic_category_ids: (item.graphicCategoryIds || []).map((c) =>
				typeof c === 'object' ? c.value : c,
			),
			design_template_ids: (item.designTemplateIds || []).map((t) =>
				typeof t === 'object' ? t.value : t,
			),
			default_location: {
				x: offsetX,
				y: offsetY,
				z: 0,
			},
			default_size: {
				width,
				height,
			},
			radial_angle: Number(item.radialAngle),
			artwork_angle: Number(item.rotation) || 0,
			//pricing_id: item.priceModel.value,
			thumbnail_image_url: state?.uploadThumbnail[index],
			target_object: {
				width: Number(item.targetObject.width),
				height: Number(item.targetObject.height),
				name: item.targetObject.name,
				center_radial_angle: 0, // hidden in UI for now — always save as 0
				mask: state?.uploadMask[index],
				thumbnail_image_url: state?.uploadThumbnail[index],
				view_url: state?.uploadSectionImages?.[index] || state?.uploadThumbnail[index],
				...(item.targetObject?.pixel_to_size != null && {
					pixel_to_size: Number(item.targetObject.pixel_to_size),
				}),
				// Where the section sits and which way it faces, for frame profiles. Written
				// only when present, so a cylindrical section's target_object is byte-identical
				// to what it has always been. Width and height are deliberately not duplicated
				// here — they are default_size above, under either model.
				...(item.placement ? { placement: item.placement } : {}),
				shape_type: shapeType,
				...(shapeType === 'circle' && item.radius != null && {
					radius: Number(item.radius),
				}),
				...(shapeType === 'polygon' && item.vertices && {
					vertices: item.vertices,
				}),
				...(item.materialType != null && {
					material_type: materialType || 'steel',
				}),
				...(item.engravingTint != null && {
					engraving_tint: Number(item.engravingTint),
				}),
				...(item.overlayOpacity != null && {
					overlay_opacity: Number(item.overlayOpacity),
				}),
				// Only set for material_type 'custom'. target_object is rebuilt from
				// scratch on every save, so omitting the key clears a pattern that was
				// switched away from — and keeps it out of 3D sections entirely.
				...(usesPattern && state?.uploadMaterialFiles?.[index] && {
					material_file: state.uploadMaterialFiles[index],
				}),
				...(materialType === 'custom' && !usesPattern && materialSource?.color && {
					material_color: materialSource.color,
				}),
			},
			...(isNeatolabs
				? {
					center_section_to_field: item.engravingSetting?.center_section_to_field,
					maintain_aspect_ratio: item.engravingSetting?.maintain_aspect_ratio,
					preview_mark_speed: Number(item.engravingSetting?.preview_mark_speed),
					marking_order: item.engravingSetting?.marking_order?.value,
					keystone_correction: {
						horizontal_ratio_percentage: Number(item.engravingSetting?.horizontal_ratio_percentage || 0),
						vertical_ratio_percentage: Number(item.engravingSetting?.vertical_ratio_percentage || 0),
					},
					preview_mark_type: item.engravingSetting?.preview_mark_type?.value,
					outline_laser_profile_id: item.engravingSetting?.outline_laser_profile_id?.value,
					hatch_profile_ids: item.engravingSetting?.hatch_profile_ids?.map((hatchId: any) => hatchId.value) || [],
				}
				: {
					center_section_to_field: null,
					maintain_aspect_ratio: null,
					preview_mark_speed: null,
					marking_order: null,
					keystone_correction: null,
					preview_mark_type: null,
					outline_laser_profile_id: null,
					hatch_profile_ids: null,
				}),
		}
	})
}

export default mapSectionToSave
